import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { apiOps } from '../lib/api'
import { isApiError } from '../lib/query/apiError'
import { keys, scopedKey } from '../lib/query/keys'
import { usePpcFreshness, type PpcFreshness } from '../lib/query/freshness'

function formatComplete(iso: string | null): string {
  if (!iso) return '—'
  // ISO is "YYYY-MM-DD"; render as "MMM D" using local-tz-independent parsing
  // (Date(iso) would shift by tz on negative offsets).
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatSyncedAt(iso: string | null): string {
  if (!iso) return ''
  const dt = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'))  // sync timestamps are UTC
  const now = new Date()
  const mins = Math.round((now.getTime() - dt.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  return `${days}d ago`
}

/**
 * Compact status chip showing the most recent complete PPC day, with a
 * refresh button that triggers a BigQuery → local DuckDB sync of the PPC
 * tables. Reads the shared `/api/ppc-freshness` query (lib/query/freshness.ts,
 * UI overhaul PR-2): one poll for the whole app, every 4 s while a refresh is
 * in flight, so mounting this chip adds no request of its own.
 */
export default function PpcFreshnessChip() {
  const query = usePpcFreshness()
  const queryClient = useQueryClient()
  const data: PpcFreshness | null = query.data ?? null
  const [actionError, setActionError] = useState<string | null>(null)
  const error = actionError ?? (query.isError ? query.error.message : null)

  async function triggerRefresh() {
    if (data?.is_refreshing) return
    setActionError(null)
    // Optimistically flip the flag so the spinner appears immediately; the
    // shared query then follows the sync at its fast cadence.
    queryClient.setQueryData<PpcFreshness>(scopedKey(keys.freshness.ppc()), (d) => (d ? { ...d, is_refreshing: true } : d))
    try {
      await apiOps.refreshPpc()
    } catch (e) {
      // 409 = a refresh is already running — the state we wanted anyway.
      if (!(isApiError(e) && e.status === 409)) setActionError(e instanceof Error ? e.message : String(e))
    }
    // Pull fresh state regardless — covers the 409 "already running" case.
    void query.refetch()
  }

  const refreshing = !!data?.is_refreshing
  const completeLabel = formatComplete(data?.last_complete_date ?? null)
  const syncedAgo = data?.last_synced_at ? formatSyncedAt(data.last_synced_at) : ''

  const tooltip = error
    ? `Freshness lookup failed: ${error}`
    : data?.last_refresh_error
      ? `Last refresh failed: ${data.last_refresh_error}`
      : data?.last_complete_date
        ? `PPC data complete through ${data.last_complete_date}` +
          (syncedAgo ? ` — synced ${syncedAgo}` : '')
        : 'No PPC data found locally'

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-1 border border-gray-300 rounded text-xs bg-white"
      title={tooltip}
    >
      <span className="text-gray-500">Data through</span>
      <span className="font-medium text-gray-900">{completeLabel}</span>
      {syncedAgo && !refreshing && (
        <span className="text-gray-400">· {syncedAgo}</span>
      )}
      {refreshing && (
        <span className="text-violet-600">· syncing…</span>
      )}
      <button
        onClick={triggerRefresh}
        disabled={refreshing}
        title={refreshing ? 'Refresh in progress' : 'Pull current data from BigQuery'}
        className="ml-1 p-0.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <RefreshCw
          size={12}
          className={`text-gray-500 ${refreshing ? 'animate-spin' : ''}`}
        />
      </button>
    </div>
  )
}
